// TextContext.js

import React, { createContext, useContext } from 'react';

// Create a Context for the text data
const TextContext = createContext();

export const TextProvider = ({ children }) => {
  const textData = {
    personalInfo: {
      name: "Mario Bozhinovski",
      position: "Cloud & Data Engineering for Connected Products · Founder, Aldea Systems",
      email: "contact@aldeasystems.com",
      location: "Ohrid, Republic of Macedonia",
      linkedin: "https://www.linkedin.com/in/mario-božinovski-2a4a7a157/",
      website: "https://mariob.cloud",
    },
    commercialRoles: [
      'Cloud & Data Engineering',
      'High-load data platforms',
      'ETL & pipelines',
      'Event-driven systems',
      'AI orchestration',
      'B2B vendor partner',
    ],
    openTo: "Open to B2B vendor collaboration through Aldea Systems—cloud, data and tooling for connected products: high-load platforms, event-driven pipelines, ETL and AI orchestration, from one engineer to a scaled team.",
    aboutMe: "I am the founder of Aldea Systems, a B2B engineering company focused on cloud and data engineering for connected products. Through Aldea I partner with engineering companies on the cloud, data and tooling layer: ingestion, pipelines, storage, dashboards and AI-assisted analysis—with clear ownership and practical delivery.",
    // High-signal keywords for ATS / AI CV reviewers (also rendered bold in the PDF)
    priorityKeywords: [
      '.NET',
      'C#',
      'Azure',
      'AI',
      'LLM',
      'RAG',
      'CAG',
      'Kubernetes',
      'Docker',
      'Git',
      'SQL',
      'MSSQL',
      'PostgreSQL',
      'Python',
      'React',
      'Microservices',
      'System Architecture',
    ],
    skills: {
        main:[
            'Microsoft .NET / C#',
            'Microsoft Azure',
            'AI / LLM / RAG / CAG',
            'Python',
            'React',
            'Kubernetes',
            'Docker',
            'Git',
            'SQL / Microsoft SQL Server (MSSQL)',
            'PostgreSQL',
            'Azure Service Bus',
            'Azure Event Hub',
            'Azure IoT Hub',
            'Azure Cosmos DB',
            'Azure Blob Storage',
            'Azure Functions',
            'Microservices',
            'Event-Driven Architecture (EDA)',
            'Extract Transform Load (ETL)',
            'Change Data Capture (CDC)',
            'Leadership',
          ],
          other: [
            'Angular',
            'Blazor',
            'Xamarin',
            'Go',
            'Jenkins',
            'TypeScript',
            'jQuery',
            'JavaScript',
            'Debezium',
            'Apache Kafka',
            'RedPanda',
            'RabbitMQ',
            'OData',
            'GraphQL',
            'WebSocket',
            'Trunk-based Development',
            'Supabase',
            'pgvector',
            'Qdrant',
            'Node.js'
          ],
          integrations: [
            'Stripe',
            'Zoom',
            'Calendly',
            'Payment Processors',
            'SMTP',
            'Email Services',
            'Third-party APIs',
            'OAuth',
            'Webhooks'
          ],
          aiTools: [
            'AI Agent Orchestration',
            'Claude',
            'Cursor',
            'ChatGPT',
            'GitHub Copilot',
            'AI Code Assistants',
            'Multi-Agent Workflows',
            'RAG/CAG Pipelines',
            'LLMs'
          ]
    },
    products: {
      url: 'https://mariob.cloud/products',
      items: [
        {
          name: 'Arrangelly',
          url: 'https://staging.arrangelly.com/',
          summary: 'All-in-one platform for music teachers—lessons, bookings, payments, content, and AI exercises.',
        },
        {
          name: 'Exposly',
          summary: 'VPS cockpit for deployments, live logs/monitoring, TLS certificates, and day-to-day server ops.',
        },
        {
          name: 'TrafoHub',
          url: 'https://trafohub.com/',
          summary: 'B2B marketplace for power transformers—verified inventory, quotes, and partner network.',
        },
        {
          name: 'Violynning',
          url: 'https://violynning.com/',
          summary: 'Music education platform with admin tools, subscriptions, bookings, and student dashboards.',
        },
      ],
    },
    experiences:[
        {
          title: 'Founder & Owner',
          company: 'Aldea Systems',
          companyUrl: 'https://aldeasystems.com/',
          duration: '2026 – Present',
          projects: [
            'B2B cloud and data engineering for connected products—high-load platforms, pipelines, ETL and AI orchestration',
            'Vendor engagements with engineering companies on the cloud, data and tooling layer after the device',
            'Architecture, delivery ownership and AI-assisted engineering on client and product work',
            'Building and shipping products including Arrangelly, TrafoHub, Violynning, Exposly, and more',
          ],
          skills: ['.NET', 'React', 'Azure', 'AI/LLM', 'RAG/CAG', 'PostgreSQL', 'ETL', 'Data Engineering', 'B2B Vendor', 'System Architecture'],
        },
        {
          title: 'Principal Software Engineer & Technical Lead',
          company: 'Damilah',
          companyUrl: 'https://damilah.com/',
          duration: '01/2023 – 06/2026',
          projects: [
            'Led technical decisions and architecture for a team of 12 in a large-scale scrum programme of 400+ people',
            'Trunk-based development',
            'Extreme programming - Pair programming',
            'DataSync of old single tenant system to new multi-tenant solution',
            'Monolith to microservices cutover with event-driven integration',
          ],
          skills: ['Azure ServiceBus', 'Azure Event Hub', 'MSSQL', 'Microservices', 'Trunk-based Development'],
        },
        {
          title: 'Senior Software Engineer',
          company: 'EPAM Systems',
          companyUrl: 'https://www.epam.com/',
          duration: '08/2022 – 01/2023',
          projects: [
            'RemoteDeployment, ServiceCockpit, AuditService, CloudToDeviceBridge, etc.',
            'Drove technical decisions and ownership for the cloud stack',
            'Planning and ownership of delivery workstreams',
          ],
          skills: ['Azure ServiceBus', 'Azure IoTHub', 'MSSQL', 'Technical Leadership'],
        },
        {
          title: 'Software Engineer',
          company: 'EPAM Systems',
          companyUrl: 'https://www.epam.com/',
          duration: '08/2021 – 08/2022',
          projects: ['Build Web application as a Desktop using Electron.NET for Tecan PC OS'],
          skills: ['Electron.NET', 'Web Application Development'],
        },
        {
          title: 'Senior Software Engineer',
          company: 'Allocate Software',
          companyUrl: 'https://www.allocatesoftware.com/',
          duration: '10/2020 – 08/2021',
          projects: [
            'Developed API from scratch for consuming OData services from HealthRoster.',
            'Led CI/CD processes and onboarded new team members.',
            'Collaborated with senior management to address blockers and deadlines.',
            'Worked on OData services for Rostering features and integration testing.',
          ],
          skills: ['CI/CD', 'OData', 'API Development', 'Autorest', 'Azure'],
        },
        {
          title: 'Full-Stack Software Engineer',
          company: 'Allocate Software',
          companyUrl: 'https://www.allocatesoftware.com/',
          duration: '12/2019 – 09/2020',
          projects: [
            'Developed MePlusMobile (Xamarin.Forms) and MePlusApi (.NET Core) for cross-platform.',
            'Refactored legacy code and implemented new features.',
            'Worked on data synchronization and HealthSuite bug fixes and deployments.',
          ],
          skills: ['Xamarin.Forms', 'Xamarin', 'API Development', 'Mobile Development'],
        },
        {
          title: 'Full-Stack Software Engineer',
          company: 'IT Crowd',
          companyUrl: 'https://itcrowd.me/',
          duration: '05/2018 – 12/2019',
          projects: [
            'Developed blockchain-based digital identity system (Farmer Connect).',
            'Created social media platform (SMP Platform) and integrated third-party APIs (Facebook, LinkedIn).',
          ],
          skills: ['Blockchain', 'ReactJS', 'API Integration'],
        },
        {
          title: 'Full-Stack Software Engineer Intern',
          company: 'IT Crowd',
          companyUrl: 'https://itcrowd.me/',
          duration: '03/2018 – 05/2018',
          projects: ['Worked on VisualGuest CRM/ERP application for a Swiss client.'],
          skills: ['ADO.NET', 'Entity Framework', 'MSSQL', 'HTML', 'CSS', 'Bootstrap'],
        },
        {
          title: 'Backend Developer Intern',
          company: 'PolarCape',
          companyUrl: 'https://www.polarcape.com/',
          duration: '09/2017 – 11/2017',
          projects: ['Developed Conference Rooms application for booking conference rooms.'],
          skills: ['Java', 'Spring Boot', 'Maven', 'Hibernate'],
        },
        {
          title: 'Bachelor’s Degree Student',
          company: 'Faculty of Electrical Engineering and Information Technologies, Skopje',
          companyUrl: 'https://feit.ukim.edu.mk/',
          duration: '2012 – 2017',
          projects: ['Developed SmartHome app for controlling non-smart devices with Raspberry Pi3 and Arduino.'],
          skills: ['Java', 'Spring Boot', 'Maven', 'Hibernate', 'MySql', 'Angular 2', 'Ionic', 'Cordova'],
        },
      ],
    languages:[
        { lang:'Macedonian: Native', color:'primary' }, 
        { lang:'English: Full Professional Proficiency', color:'primary' },
        { lang:'Bulgarian: Limited Working Proficiency', color:'secondary' }, 
        { lang:'Serbian: Full Professional Proficiency', color:'secondary' }, 
        ],
    education: {
      degree: "Bachelor’s Degree",
      institution: "Faculty of Electrical Engineering and Information Technologies, Skopje",
      duration: "2012 – 2017",
    },
    certifications:[
        {
            title: "Silver Award - The OLX Group Challenge",
            issuer: "Codility",
            issued: "Jan 2021",
            expired: "Nov 2022",
            credentialId: "certE9TZBP-UXFVFRZJ9N94TTN3",
            link: "https://app.codility.com/cert/view/certE9TZBP-UXFVFRZJ9N94TTN3/",
            logo: "https://www.codility.com/wp-content/uploads/2020/03/On_light-png.webp",
          },
      ]
  };

  

  return (
    <TextContext.Provider value={textData}>
      {children}
    </TextContext.Provider>
  );
};

// Custom hook to use the text data in other components
export const useText = () => {
  return useContext(TextContext);
};
